import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ArrowRight, User, Mail, Phone, Calendar, ShoppingBag,
  CheckCircle, XCircle, Shield, Package, Clock, CreditCard,
  ChevronLeft, ChevronRight, ShoppingCart, ImageOff,
} from 'lucide-react'
import { usersService } from '@/services/usersService'
import { usePagination } from '@/hooks/usePagination'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import StatusBadge from '@/components/shared/StatusBadge'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { formatCurrency, formatDate, formatRelativeTime, getLocalizedField, ROLE_LABELS } from '@/utils/formatters'
import { ROUTES } from '@/constants/routes'

const INFO = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-3 py-3" style={{ borderBottom: '1px solid #F3F3F3' }}>
    <div
      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
      style={{ background: 'rgba(0,6,102,0.06)', color: '#000666' }}
    >
      <Icon size={15} />
    </div>
    <div>
      <p className="text-xs text-gray-400 mb-0.5" style={{ fontFamily: 'Tajawal, sans-serif' }}>{label}</p>
      <p className="text-sm font-bold text-gray-800" style={{ fontFamily: 'Cairo, sans-serif' }}>{value || '—'}</p>
    </div>
  </div>
)

export default function UserDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { page, limit, setPage } = usePagination(1, 10)
  const [dialog, setDialog] = useState({ open: false, action: null })

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: () => usersService.getById(id),
    retry: 1,
  })

  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ['user-orders', id, page],
    queryFn: () => usersService.getOrders(id, { page, limit }),
    enabled: !!id,
    retry: 1,
  })

  const { data: cart, isLoading: cartLoading } = useQuery({
    queryKey: ['user-cart', id],
    queryFn: () => usersService.getCart(id),
    enabled: !!id,
    retry: 1,
  })

  const orders = ordersData?.data || []
  const meta   = ordersData?.meta || {}

  const toggleStatus = useMutation({
    mutationFn: ({ isActive }) => usersService.updateStatus(id, isActive),
    onSuccess: (_, { isActive }) => {
      toast.success(isActive ? 'تم تفعيل الحساب' : 'تم إيقاف الحساب')
      qc.invalidateQueries({ queryKey: ['user', id] })
      qc.invalidateQueries({ queryKey: ['users'] })
      setDialog({ open: false, action: null })
    },
    onError: () => toast.error('حدث خطأ، يرجى المحاولة مجدداً'),
  })

  if (userLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <LoadingSpinner />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="text-center py-24" style={{ fontFamily: 'Tajawal, sans-serif', color: '#767683' }}>
        المستخدم غير موجود
      </div>
    )
  }

  const roleLabel = ROLE_LABELS[user.role]
  const isActive  = user.isActive !== false

  // ── Status badge inline styles ────────────────────────────────────────────
  const statusStyle = isActive
    ? { background: 'rgba(27,109,36,0.1)', color: '#1B6D24' }
    : { background: 'rgba(186,26,26,0.1)', color: '#BA1A1A' }

  return (
    <div className="space-y-6">

      {/* ── Back + Actions bar ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(ROUTES.USERS)}
          className="flex items-center gap-2 text-sm font-bold transition-colors"
          style={{ color: '#000666', fontFamily: 'Cairo, sans-serif' }}
        >
          <ArrowRight size={16} />
          العودة إلى المستخدمين
        </button>

        <div className="flex items-center gap-3">
          {isActive ? (
            <button
              onClick={() => setDialog({ open: true, action: 'suspend' })}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all"
              style={{ background: 'rgba(186,26,26,0.08)', color: '#BA1A1A', fontFamily: 'Cairo, sans-serif' }}
            >
              <XCircle size={16} />
              إيقاف الحساب
            </button>
          ) : (
            <button
              onClick={() => setDialog({ open: true, action: 'activate' })}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all"
              style={{ background: 'rgba(27,109,36,0.08)', color: '#1B6D24', fontFamily: 'Cairo, sans-serif' }}
            >
              <CheckCircle size={16} />
              تفعيل الحساب
            </button>
          )}
        </div>
      </div>

      {/* ── Main content grid ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

        {/* Profile card */}
        <div className="xl:col-span-1 space-y-4">
          <div className="hg-card p-6">
            {/* Avatar + name */}
            <div className="flex flex-col items-center text-center mb-6">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center text-white text-2xl font-black mb-3"
                style={{ background: 'linear-gradient(135deg, #000666 0%, #1A237E 100%)' }}
              >
                {user.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <h1
                className="text-xl font-black text-gray-900 mb-1"
                style={{ fontFamily: 'Cairo, sans-serif' }}
              >
                {user.name}
              </h1>
              <div className="flex items-center gap-2 flex-wrap justify-center">
                <span
                  className="px-3 py-1 rounded-full text-xs font-bold"
                  style={{ background: 'rgba(0,6,102,0.08)', color: '#000666', fontFamily: 'Cairo, sans-serif' }}
                >
                  {roleLabel?.ar || user.role}
                </span>
                <span
                  className="px-3 py-1 rounded-full text-xs font-bold"
                  style={{ ...statusStyle, fontFamily: 'Cairo, sans-serif' }}
                >
                  {isActive ? 'نشط' : 'موقوف'}
                </span>
              </div>
            </div>

            {/* Info rows */}
            <INFO label="البريد الإلكتروني"  value={user.email}     icon={Mail} />
            <INFO label="رقم الجوال"          value={user.phone}     icon={Phone} />
            <INFO label="الدور"               value={roleLabel?.ar || user.role} icon={Shield} />
            <INFO label="تاريخ التسجيل"       value={formatDate(user.createdAt)} icon={Calendar} />
            <div className="pt-1">
              <INFO label="حالة التحقق"
                value={user.isVerified ? 'تم التحقق من البريد' : 'غير محقق'}
                icon={CheckCircle}
              />
            </div>
          </div>

          {/* Quick stats */}
          <div className="hg-card p-5">
            <h3 className="font-black text-sm text-gray-500 mb-4" style={{ fontFamily: 'Cairo, sans-serif' }}>
              إحصائيات سريعة
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'إجمالي الطلبات', value: meta.total ?? '—', color: '#000666', icon: ShoppingBag },
                { label: 'آخر نشاط',        value: formatRelativeTime(user.updatedAt || user.createdAt), color: '#FF6D00', icon: Clock },
              ].map(({ label, value, color, icon: Icon }) => (
                <div
                  key={label}
                  className="rounded-xl p-3 flex flex-col gap-2"
                  style={{ background: `${color}08` }}
                >
                  <Icon size={18} style={{ color }} />
                  <p className="text-lg font-black" style={{ color, fontFamily: 'Cairo, sans-serif' }}>{value}</p>
                  <p className="text-xs text-gray-500" style={{ fontFamily: 'Tajawal, sans-serif' }}>{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Orders history */}
        <div className="xl:col-span-2 hg-card p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-black text-lg" style={{ color: '#000666', fontFamily: 'Cairo, sans-serif' }}>
              سجل الطلبات
            </h2>
            {meta.total > 0 && (
              <span className="text-xs text-gray-400" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                {meta.total} طلب إجمالاً
              </span>
            )}
          </div>

          {ordersLoading ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-16">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                style={{ background: 'rgba(0,6,102,0.06)', color: '#000666' }}
              >
                <Package size={28} />
              </div>
              <p className="text-gray-400 text-sm" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                لا توجد طلبات لهذا المستخدم
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr style={{ borderBottom: '2px solid #F3F3F3' }}>
                      {['رقم الطلب', 'المنتجات', 'الإجمالي', 'الدفع', 'الحالة', 'التاريخ'].map((h) => (
                        <th
                          key={h}
                          className="pb-3 text-right text-xs font-bold text-gray-400 uppercase tracking-wide"
                          style={{ fontFamily: 'Tajawal, sans-serif', paddingLeft: 12, paddingRight: 4 }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr
                        key={order.id}
                        className="hover:bg-gray-50 transition-colors"
                        style={{ borderBottom: '1px solid #F9F9F9' }}
                      >
                        <td className="py-3 px-1">
                          <button
                            onClick={() => navigate(`/orders/${order.orderNumber}`)}
                            className="font-black text-sm hover:underline transition-colors"
                            style={{ color: '#000666', fontFamily: 'Cairo, sans-serif', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          >
                            {order.orderNumber}
                          </button>
                        </td>
                        <td className="py-3 px-1">
                          <div className="flex items-center gap-1.5">
                            <Package size={14} className="text-gray-400" />
                            <span className="text-sm text-gray-600" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                              {order._count?.items ?? order.items?.length ?? '—'} منتج
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-1">
                          <span className="font-black text-sm" style={{ color: '#FF6D00', fontFamily: 'Cairo, sans-serif' }}>
                            {formatCurrency(order.total)}
                          </span>
                        </td>
                        <td className="py-3 px-1">
                          <span className="text-xs text-gray-500 flex items-center gap-1" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                            <CreditCard size={12} />
                            {order.paymentMethod || '—'}
                          </span>
                        </td>
                        <td className="py-3 px-1">
                          <StatusBadge status={order.status} type="order" lang="ar" />
                        </td>
                        <td className="py-3 px-1">
                          <span className="text-xs text-gray-400" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                            {formatRelativeTime(order.createdAt, 'ar-EG')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {meta.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: '1px solid #F3F3F3' }}>
                  <span className="text-xs text-gray-400" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                    صفحة {meta.page} من {meta.totalPages}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={!meta.hasPrev}
                      className="w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-30 transition-all"
                      style={{ background: '#F3F3F3' }}
                    >
                      <ChevronRight size={16} />
                    </button>
                    <button
                      onClick={() => setPage(p => p + 1)}
                      disabled={!meta.hasNext}
                      className="w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-30 transition-all"
                      style={{ background: '#F3F3F3' }}
                    >
                      <ChevronLeft size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Cart section ───────────────────────────────────────────────────── */}
      <div className="hg-card p-5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(255,109,0,0.08)', color: '#FF6D00' }}
            >
              <ShoppingCart size={17} />
            </div>
            <div>
              <h2 className="font-black text-base" style={{ color: '#1A1C1C', fontFamily: 'Cairo, sans-serif' }}>
                سلة التسوق الحالية
              </h2>
              <p className="text-xs text-gray-400" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                المنتجات المضافة ولم يتم طلبها بعد
              </p>
            </div>
          </div>
          {cart?.itemCount > 0 && (
            <div
              className="px-3 py-1.5 rounded-xl text-sm font-black"
              style={{ background: 'rgba(255,109,0,0.08)', color: '#FF6D00', fontFamily: 'Cairo, sans-serif' }}
            >
              {cart.itemCount} منتج · {formatCurrency(cart.subtotal)}
            </div>
          )}
        </div>

        {cartLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : !cart || cart.items?.length === 0 ? (
          <div className="text-center py-12">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3"
              style={{ background: '#F8F8FB', color: '#C4C4C4' }}
            >
              <ShoppingCart size={24} />
            </div>
            <p className="text-sm text-gray-400" style={{ fontFamily: 'Tajawal, sans-serif' }}>
              السلة فارغة
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {cart.items.map((item) => {
              const image = item.variant?.imageUrl || item.product?.images?.[0]?.url
              const price = Number(item.priceSnapshot)
              const lineTotal = price * item.quantity
              const isUnavailable =
                item.product?.status !== 'active' ||
                (item.variant && !item.variant.isActive)

              return (
                <div
                  key={item.id}
                  className="flex items-center gap-4 p-3 rounded-2xl transition-colors"
                  style={{
                    border: '1px solid #F3F3F3',
                    background: isUnavailable ? 'rgba(186,26,26,0.02)' : '#FAFAFA',
                    borderColor: isUnavailable ? 'rgba(186,26,26,0.15)' : '#F3F3F3',
                  }}
                >
                  {/* Product image */}
                  <div
                    className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center"
                    style={{ background: '#E2E2E2' }}
                  >
                    {image ? (
                      <img src={image} alt={getLocalizedField(item.product?.name)} className="w-full h-full object-cover" />
                    ) : (
                      <ImageOff size={20} style={{ color: '#C4C4C4' }} />
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <p
                      className="font-bold text-sm truncate"
                      style={{ color: '#1A1C1C', fontFamily: 'Cairo, sans-serif' }}
                    >
                      {getLocalizedField(item.product?.name) || '—'}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap mt-0.5">
                      {item.variant && (
                        <span
                          className="text-xs px-2 py-0.5 rounded-md"
                          style={{ background: 'rgba(0,6,102,0.06)', color: '#000666', fontFamily: 'Tajawal, sans-serif' }}
                        >
                          {item.variant.name}
                        </span>
                      )}
                      <span className="text-xs text-gray-400" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                        {getLocalizedField(item.product?.vendor?.storeName)}
                      </span>
                      {isUnavailable && (
                        <span
                          className="text-xs font-bold px-2 py-0.5 rounded-md"
                          style={{ background: 'rgba(186,26,26,0.08)', color: '#BA1A1A', fontFamily: 'Cairo, sans-serif' }}
                        >
                          غير متاح
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Qty + price */}
                  <div className="text-left flex-shrink-0">
                    <p
                      className="font-black text-sm"
                      style={{ color: '#FF6D00', fontFamily: 'Cairo, sans-serif' }}
                    >
                      {formatCurrency(lineTotal)}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                      {item.quantity} × {formatCurrency(price)}
                    </p>
                  </div>
                </div>
              )
            })}

            {/* Total row */}
            <div
              className="flex items-center justify-between px-4 py-3 rounded-xl mt-1"
              style={{ background: 'rgba(255,109,0,0.05)', border: '1px solid rgba(255,109,0,0.12)' }}
            >
              <span className="text-sm font-bold text-gray-500" style={{ fontFamily: 'Cairo, sans-serif' }}>
                الإجمالي ({cart.itemCount} منتج)
              </span>
              <span
                className="text-base font-black"
                style={{ color: '#FF6D00', fontFamily: 'Cairo, sans-serif' }}
              >
                {formatCurrency(cart.subtotal)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ── Confirm dialog ─────────────────────────────────────────────────── */}
      <ConfirmDialog
        open={dialog.open}
        title={dialog.action === 'suspend' ? 'إيقاف الحساب' : 'تفعيل الحساب'}
        message={
          dialog.action === 'suspend'
            ? `هل تريد إيقاف حساب ${user.name}؟ لن يتمكن من تسجيل الدخول.`
            : `هل تريد إعادة تفعيل حساب ${user.name}؟`
        }
        variant={dialog.action === 'suspend' ? 'danger' : 'warning'}
        loading={toggleStatus.isPending}
        onConfirm={() => toggleStatus.mutate({ isActive: dialog.action === 'activate' })}
        onCancel={() => setDialog({ open: false, action: null })}
      />
    </div>
  )
}
